import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const TeamSection = () => {
  const teamMembers = [
    {
      name: "Sarah Chen",
      role: "CEO & Co-Founder",
      bio: "Former product lead at Google, passionate about using technology to strengthen human relationships. PhD in Human-Computer Interaction.",
      image: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=400&h=400&fit=crop",
      expertise: ["Product Strategy", "AI Ethics", "User Experience"],
      social: { linkedin: "#", twitter: "#" }
    },
    {
      name: "Marcus Rodriguez",
      role: "CTO & Co-Founder",
      bio: "AI researcher with 15+ years in machine learning. Previously led recommendation systems at Netflix and Amazon.",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop",
      expertise: ["Machine Learning", "System Architecture", "Data Science"],
      social: { linkedin: "#", github: "#" }
    },
    {
      name: "Dr. Amara Okafor",
      role: "Head of Cultural Research",
      bio: "Cultural anthropologist specializing in gift-giving traditions across cultures. Author of 'The Language of Gifts'.",
      image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400&h=400&fit=crop",
      expertise: ["Cultural Anthropology", "Global Traditions", "Research"],
      social: { linkedin: "#", twitter: "#" }
    },
    {
      name: "James Park",
      role: "Head of Design",
      bio: "Award-winning designer focused on emotional design and accessibility. Former design director at Airbnb.",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop",
      expertise: ["UX Design", "Accessibility", "Design Systems"],
      social: { linkedin: "#", dribbble: "#" }
    },
    {
      name: "Dr. Lisa Thompson",
      role: "Head of Psychology",
      bio: "Licensed psychologist specializing in relationships and social connections. Consultant for relationship-focused apps.",
      image: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=400&h=400&fit=crop",
      expertise: ["Relationship Psychology", "Behavioral Science", "User Research"],
      social: { linkedin: "#", twitter: "#" }
    },
    {
      name: "Alex Kumar",
      role: "Head of Engineering",
      bio: "Full-stack engineer with expertise in scalable systems. Previously built infrastructure at Stripe and Shopify.",
      image: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=400&h=400&fit=crop",
      expertise: ["Full-Stack Development", "System Scalability", "Security"],
      social: { linkedin: "#", github: "#" }
    }
  ];

  const getSocialIcon = (platform) => {
    const iconMap = {
      linkedin: "Linkedin",
      twitter: "Twitter",
      github: "Github",
      dribbble: "Dribbble"
    };
    return iconMap[platform] || "Link";
  };

  return (
    <section className="py-16 lg:py-24 bg-gradient-warm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="font-heading text-3xl lg:text-5xl font-bold text-accent-800 mb-6">
            Meet Our Team
          </h2>
          <p className="text-lg lg:text-xl text-accent-600 max-w-3xl mx-auto leading-relaxed">
            Our diverse team brings together expertise in technology, psychology, cultural research, and design to create meaningful gift-giving experiences.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {teamMembers.map((member, index) => (
            <div key={index} className="group hover-lift">
              <div className="bg-white rounded-2xl p-6 shadow-md border border-accent-100 h-full transition-all duration-300 group-hover:shadow-lg group-hover:border-primary-200">
                {/* Profile Image */}
                <div className="relative mb-6">
                  <div className="w-24 h-24 mx-auto rounded-full overflow-hidden ring-4 ring-primary-100 group-hover:ring-primary-200 transition-all duration-300">
                    <Image
                      src={member.image}
                      alt={`${member.name} - ${member.role}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                {/* Member Info */}
                <div className="text-center mb-4">
                  <h3 className="font-heading text-xl font-bold text-accent-800 mb-1">
                    {member.name}
                  </h3>
                  <p className="text-primary-600 font-medium mb-3">
                    {member.role}
                  </p>
                  <p className="text-accent-600 text-sm leading-relaxed mb-4">
                    {member.bio}
                  </p>
                </div>

                {/* Expertise Tags */}
                <div className="flex flex-wrap gap-2 justify-center mb-4">
                  {member.expertise.map((skill, skillIndex) => (
                    <span
                      key={skillIndex}
                      className="px-3 py-1 bg-primary-50 text-primary-700 text-xs font-medium rounded-full"
                    >
                      {skill}
                    </span>
                  ))}
                </div>

                {/* Social Links */}
                <div className="flex justify-center space-x-3">
                  {Object.entries(member.social).map(([platform, url]) => (
                    <a
                      key={platform}
                      href={url}
                      className="w-8 h-8 bg-accent-100 rounded-full flex items-center justify-center text-accent-600 hover:bg-primary-100 hover:text-primary-600 transition-colors duration-200"
                      aria-label={`${member.name} on ${platform}`}
                    >
                      <Icon name={getSocialIcon(platform)} size={16} />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Team Stats */}
        <div className="bg-white rounded-2xl p-8 lg:p-12 shadow-lg border border-accent-100">
          <div className="text-center mb-8">
            <h3 className="font-heading text-2xl lg:text-3xl font-bold text-accent-800 mb-4">
              Our Team by the Numbers
            </h3>
            <p className="text-lg text-accent-600">
              Diversity and expertise that drives innovation
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Icon name="Users" size={28} className="text-primary-600" />
              </div>
              <p className="text-2xl font-bold text-accent-800 mb-1">50+</p>
              <p className="text-accent-600">Team Members</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-secondary-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Icon name="Globe" size={28} className="text-secondary-600" />
              </div>
              <p className="text-2xl font-bold text-accent-800 mb-1">15</p>
              <p className="text-accent-600">Countries</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-warning-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Icon name="Award" size={28} className="text-warning-600" />
              </div>
              <p className="text-2xl font-bold text-accent-800 mb-1">25+</p>
              <p className="text-accent-600">Languages</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-success-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Icon name="Briefcase" size={28} className="text-success-600" />
              </div>
              <p className="text-2xl font-bold text-accent-800 mb-1">100+</p>
              <p className="text-accent-600">Years Combined Experience</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TeamSection;